<template>
	<div class="grid-screen overflow-hidden" ref="viewport user-select-none">
		<!-- Header -->
		<header
			class="fixed top-0 w-full z-50 flex flex-row justify-between px-2 pt-2"
			v-if="original_image && (image_ready || blur_loaded) && !loading"
		>
			<GridControls
				@zoom-in="updateZoom(-zoom_step)"
				@zoom-out="updateZoom(zoom_step)"
				@open-search-modal="show_search_modal = true"
				:zoom="zoom_level"
				:can_zoom_in="zoom_level != max_zoom"
				:can_zoom_out="zoom_level != min_zoom"
			>
			</GridControls>

			<div class="flex flex-row gap-2 justify-end">
				<NavButton
					icon="/images/value-drivers/4.svg"
					text_key="knowledge"
					icon_alt="Knowledge icon"
					icon_size="20"
					icon_classes="h-full py-2.5 aspect-square"
					:onClick="() => openDriverModal(5)"
				/>

				<PortfolioToggleButton
					v-if="hasInnovationHotspots"
					portfolio-type="innovation"
					@filter-change="handleFilterChange"
				/>
				<PortfolioToggleButton
					v-if="hasWaterSavingHotspots"
					portfolio-type="water-saving"
					@filter-change="handleFilterChange"
				/>
				<FiltersMenu @filter-change="handleFilterChange" />
			</div>
		</header>

		<transition name="image-fade">
			<div
				v-if="
					!has_placeholder &&
					original_image &&
					(image_ready || blur_loaded) &&
					!loading
				"
				id="grid-image-test"
				class="grid-image fixed top-0 left-0 m-0 h-32 w-32 bg-contain bg-center bg-no-repeat p-0"
				draggable="false"
				:style="getImageStyle()"
			>
				<!-- Sharp image overlay -->
				<div v-if="original_image" :style="getMainImageStyle()"></div>

				<div class="relative h-full w-full">
					<PegComponent
						v-for="hotspot in visible_hotspots"
						:key="`hotspot-${hotspot.id}`"
						v-bind="hotspot"
						:viewport_width="current_image_width"
						:viewport_height="current_image_height"
						:zoom="zoom_level"
					>
					</PegComponent>
				</div>
			</div>
		</transition>

		<transition name="loader-fade">
			<div
				v-if="loading && !show_notice && !has_placeholder"
				class="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-br from-slate-700 to-slate-950"
			>
				<div class="text-center max-w-sm">
					<div class="relative h-[120px]">
						<div
							v-if="loadedCategory && loadedCategory.image"
							class="w-24 h-24 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl flex items-center justify-center mx-auto border border-blue-100/50 shadow-sm absolute top-0 left-1/2 transform -translate-x-1/2"
						>
							<img
								:src="loadedCategory.image || '/images/layout/placeholder.jpeg'"
								alt="selected category"
								class="w-12"
							/>
						</div>

						<div
							v-if="loadedCategory && loadedCategory.title"
							class="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-full"
						>
							<div
								class="bg-white px-3 py-1 rounded-full text-xs font-medium text-blue-600 border border-blue-100 shadow-sm inline-block"
							>
								{{ loadedCategory.title }}
							</div>
						</div>
					</div>

					<div class="h-[120px] flex flex-col justify-center">
						<div class="h-8 flex items-center justify-center">
							<transition name="fade" appear>
								<h1
									v-if="loadedSector && loadedSector.title"
									class="text-3xl font-semibold text-white mb-6 mt-12"
								>
									{{ loadedSector.title }}
								</h1>
							</transition>
						</div>
					</div>
				</div>
			</div>
		</transition>

		<!-- Placeholder -->
		<div
			v-if="has_placeholder"
			class="fixed z-3000 flex h-screen-dynamic w-screen-dynamic items-center justify-center"
			:style="{
				backgroundImage: blur_hash_image
					? 'url(' + blur_hash_image + ')'
					: 'none',
			}"
		>
			<div class="text-4xl font-bold tracking-wide">
				{{ getTranslations("coming soon") }}
			</div>
		</div>

		<!-- Error Display -->
		<div
			v-if="apiStatus === 'error' || apiStatus === 'failed'"
			class="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-br from-stone-100 to-stone-50"
		>
			<ErrorBlock
				:apiStatus="apiStatus"
				:errorMessage="errorMessage"
				:getTranslations="getTranslations"
			/>
			<button
				@click="goBack"
				class="mt-8 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg cursor-pointer hover:bg-blue-700 transition-colors duration-200 shadow-md hover:shadow-lg"
			>
				{{ getTranslations("home") || "Home" }}
			</button>
		</div>

		<!-- Modals -->
		<ValueDriverModal
			v-model:isVisible="driverModalVisible"
			:valueDriverId="valueDriverId"
		/>

		<HelpModal v-if="original_image" v-model:isVisible="show_notice">
		</HelpModal>

		<LanguageModal
			v-if="show_language_modal"
			:toggleLanguageModal="toggleLanguageModal"
		/>
		<CategoryModal
			v-if="show_category_modal"
			:toggleCategoryModal="toggleCategoryModal"
		/>
		<InfoModal
			:isVisible="show_info_modal"
			:toggleInfoModal="toggleInfoModal"
		/>

		<SearchModal
			v-if="original_image"
			v-model:isVisible="show_search_modal"
			:hotspots="visible_hotspots"
		>
		</SearchModal>

		<ShareModal
			:toggleShareModal="toggleShareModal"
			:isVisible="show_share_modal"
		/>

		<EmptyStateModal
			v-model:isVisible="show_empty_state_modal"
			@open-category-modal="openCategoryModal"
		/>

		<!-- Offscreen Indicators -->
		<OffscreenHotspotsIndicator
			:offscreenTop="offscreen_top"
			:offscreenBottom="offscreen_bottom"
			:offscreenLeft="offscreen_left"
			:offscreenRight="offscreen_right"
		>
		</OffscreenHotspotsIndicator>

		<!-- Footer -->
		<footer
			v-if="original_image && (image_ready || blur_loaded) && !loading"
			class="fixed bottom-0 left-0 flex flex-row justify-between w-screen-dynamic px-2 pb-2"
		>
			<div class="flex flex-row gap-2 justify-start">
				<NavButton
					icon="/images/icons/arrow_left_alt.svg"
					text_key="back"
					icon_size="14"
					icon_classes="rotate-180 h-full py-3"
					hide_on_mobile
					text_classes="capitalize"
					:onClick="goBack"
				/>
				<NavButton
					icon="/images/icons/category_search.svg"
					text_key="choose category"
					icon_size="20"
					icon_classes="h-full py-2.5"
					text_classes="capitalize"
					:onClick="toggleCategoryModal"
				/>
				<NavButton
					icon="/images/icons/info.svg"
					:text_key="currentSector?.title"
					icon_size="20"
					icon_classes="h-full py-2.5"
					text_classes="capitalize"
					:onClick="toggleInfoModal"
				/>
			</div>
			<div class="flex flex-row gap-2 justify-end">
				<NavButton
					icon="/images/icons/share.svg"
					text_key="share"
					icon_size="16"
					icon_classes="h-full py-2.5"
					text_classes="capitalize"
					:onClick="toggleShareModal"
				/>
				<NavButton
					icon="/images/icons/help.svg"
					text_key="help"
					icon_size="18"
					icon_classes="h-full py-2.5"
					text_classes="capitalize"
					:onClick="openHelpModal"
				/>
				<NavButton
					customClass="ga4-contact-button"
					icon="/images/icons/info.svg"
					text_key="contact"
					icon_size="18"
					hide_on_mobile
					icon_classes="h-full py-2.5"
					text_classes="capitalize"
					:onClick="goToContact"
				/>
				<NavButton
					:icon="activeLanguage?.flag"
					:text="activeLanguage?.two_digit_code"
					:icon_alt="`Flag of ${activeLanguage?.title}`"
					icon_size="20"
					icon_classes="h-full py-2.5"
					text_classes="uppercase"
					is_language_flag
					:onClick="toggleLanguageModal"
				/>
			</div>
		</footer>

		<!-- Router View -->
		<router-view
			v-if="original_image && (image_ready || blur_loaded) && !loading"
			:key="$route.path"
		></router-view>
	</div>
</template>
<script src="./GridScreen.js"></script>
<style scoped lang="scss" src="./GridScreen.scss"></style>
